"use client";

import { useAuth } from "@/contexts/AuthContext";
import { useAppPopupManager } from "@/contexts/AppPopupManagerContext";
import { PopupIdentifier } from "@/contexts/config/app_popup_manager_context.types";
import {
  getDocumentStatusMeta,
  STATUS_VARIANT_CLASSES,
} from "@/utils/getDocumentStatusLabel";
import {
  IconAlertTriangle,
  IconClock,
  IconX,
} from "@tabler/icons-react";

export function KYCStatusUpdate() {
  const { user, businessDetails, isDeveloper, isRealtor } = useAuth();
  const { closeCurrentPopup, triggerPopup } = useAppPopupManager();

  if (!isDeveloper && !isRealtor) {
    return null;
  }

  const kycStatus = (user?.kyc_status || "").toLowerCase();
  const businessStatus = (businessDetails?.status || "").toLowerCase();
  const isVerified = kycStatus === "verified" || businessStatus === "verified";

  // Nothing to report to a verified account
  if (isVerified) {
    return null;
  }

  const documents = businessDetails?.documents ?? [];

  // Only report once the user has submitted at least one document
  if (documents.length === 0) {
    return null;
  }

  const anyRejected = documents.some(
    (doc) => getDocumentStatusMeta(doc).variant === "rejected",
  );

  return (
    <div className="p-6 sm:p-7">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
              anyRejected
                ? "bg-rose-100 text-rose-600"
                : "bg-[#E8F2F4] text-[#153e46]"
            }`}
          >
            {anyRejected ? (
              <IconAlertTriangle size={20} />
            ) : (
              <IconClock size={20} />
            )}
          </span>
          <div>
            <h2 className="text-base font-bold text-slate-900">
              {anyRejected
                ? "Action required on your KYC documents"
                : "KYC verification in progress"}
            </h2>
            <p className="mt-1 text-xs text-slate-500 leading-relaxed">
              {anyRejected
                ? "One or more of your documents were rejected. Re-upload them to continue your verification."
                : "Your documents have been submitted and are awaiting review. Here is their current status."}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={closeCurrentPopup}
          aria-label="Close"
          className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <IconX size={18} />
        </button>
      </div>

      <ul className="mt-5 space-y-3">
        {documents.map((doc) => {
          const meta = getDocumentStatusMeta(doc);
          return (
            <li
              key={doc.id}
              className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4"
            >
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-800">
                  {String(doc.type).toUpperCase()}
                </p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
                  {meta.description}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  STATUS_VARIANT_CLASSES[meta.variant] ??
                  STATUS_VARIANT_CLASSES.default
                }`}
              >
                {meta.label}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-5">
        <button
          type="button"
          onClick={closeCurrentPopup}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
        >
          Close
        </button>
        <button
          type="button"
          onClick={() =>
            triggerPopup(PopupIdentifier.KYC_DEV, { overrideActive: true })
          }
          className="inline-flex items-center justify-center rounded-xl bg-[#153e46] px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#0f2e34]"
        >
          {anyRejected ? "Re-upload documents" : "View documents"}
        </button>
      </div>
    </div>
  );
}

export default KYCStatusUpdate;
